import React, { useCallback, useEffect, useState } from 'react';
import { branchesList, readAllBranches } from '../lib/chatLink';
import { BranchIcon, TickIcon, answeredLine, inBranch } from './ChatBranch';

/**
 * THE BRANCHES LIST - every branch I follow, newest reply first, in the
 * conversation pane (the whole screen on a phone). Gordon: a "Branches"
 * entry at the top of the chat list, with its badge and an Unread filter of
 * its own. The one extra place to look, and its count is part of the one
 * badge, so it never becomes a second inbox to forget.
 *
 * Each card says where the branch is, what the message said, the last two
 * replies and how many are new; opening one opens its conversation with the
 * branch beside it. "Mark all read" reads every branch I follow.
 *
 * 096: a branch can be marked answered, so the filter is Open (the default -
 * what still wants looking at) / Answered / All, with Unread beside them;
 * an answered card wears a small tick. Before 096 the server says
 * answered_ready: false and the filter is All / Unread, as it was.
 */

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/** "#Audit team · Boxes", "Rita Gatt · Billing", "Just you". */
export const whereOf = (c = {}) => (c.kind === 'group'
  ? `#${c.name || 'Group'}${c.topic ? ` · ${c.topic}` : ''}`
  : `${c.with?.is_me ? 'Just you' : (c.with?.name || 'Somebody')}${c.topic ? ` · ${c.topic}` : ''}`);

export default function BranchesInbox({ onOpen, onBack, onChanged, tick = 0 }) {
  // 'open' | 'answered' | 'all' | 'unread'. Open is the default once the
  // server can tell them apart; until it says so the list is All.
  const [filter, setFilter] = useState('open');
  const [answeredOn, setAnsweredOn] = useState(null);
  const [got, setGot] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await branchesList({ filter: filter === 'all' ? null : filter });
      setGot(data);
      setAnsweredOn(data?.answered_ready === true);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your branches');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load, tick]);

  const readAll = async () => {
    setBusy(true);
    try {
      await readAllBranches();
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not mark them read');
    } finally {
      setBusy(false);
    }
  };

  const list = got?.branches || [];
  const filters = answeredOn
    ? [['open', 'Open'], ['answered', 'Answered'], ['all', 'All'], ['unread', 'Unread']]
    : [['all', 'All'], ['unread', 'Unread']];
  // Before 096 "Open" is not a filter the server knows; the list is All.
  const checked = answeredOn === false && filter === 'open' ? 'all' : filter;
  const emptyWords = () => {
    if (filter === 'unread') return 'You are all caught up - no new replies.';
    if (filter === 'answered' && answeredOn) return 'No branch has been marked answered yet.';
    if (filter === 'open' && answeredOn) return 'Nothing open - every branch you follow has been marked answered.';
    return 'No branches yet. Press ↩ beside a message to answer it on its own; the branches you start, '
      + 'answer or follow are listed here.';
  };
  return (
    <section className="ch-inbox" aria-label="Branches">
      <header className="ch-thread-head ch-inbox-head">
        <button type="button" className="ch-back ch-back-narrow" onClick={onBack}>‹ All</button>
        <span className="ch-avatar ch-avatar-room ch-head-avatar ch-inbox-mark" aria-hidden="true"><BranchIcon size={16} /></span>
        <div className="ch-room-titles">
          <h2 className="ch-thread-name">Branches</h2>
          <p className="ch-head-sub"><span>Replies to messages, in branches you follow</span></p>
        </div>
        <div className="ch-thread-actions">
          <button type="button" className="ch-flat ch-small" disabled={busy || !(got?.unread > 0)} onClick={readAll}>
            Mark all read
          </button>
        </div>
      </header>
      <div className="ch-filters ch-inbox-filters" role="radiogroup" aria-label="Show">
        {filters.map(([v, label]) => (
          <button key={v} type="button" role="radio" className="ch-filter" aria-checked={checked === v}
            data-filter={v} onClick={() => setFilter(v)}>
            {label}
            {v === 'unread' && got?.unread > 0 && <span className="ch-filter-count">{got.unread > 99 ? '99+' : got.unread}</span>}
          </button>
        ))}
      </div>
      {error && <p className="ch-attach-error" role="alert">{error}</p>}
      {!got && !error && <p className="ch-empty">Loading…</p>}
      {got && list.length === 0 && <p className="ch-empty">{emptyWords()}</p>}
      <ul className="ch-list ch-inbox-list">
        {list.map((b) => (
          <li key={`${b.scope}-${b.parent_id}`}>
            <button type="button" className="ch-inbox-card" data-unread={b.unread > 0 || undefined}
              data-answered={b.answered ? 'true' : undefined} onClick={() => onOpen(b)}>
              <span className="ch-inbox-top">
                <span className="ch-inbox-where">{whereOf(b.conversation)}</span>
                <span className="ch-inbox-when">{when(b.last_at)}</span>
              </span>
              <span className="ch-inbox-parent" data-deleted={b.parent?.deleted || undefined}>
                <strong>{b.parent?.mine ? 'You' : b.parent?.author_name}:</strong> {b.parent?.snippet}
              </span>
              {(b.last || []).map((x) => (
                <span className="ch-inbox-reply" key={x.id}>
                  <span className="ch-inbox-reply-who">{x.mine ? 'You' : x.author_name}:</span> {x.snippet}
                </span>
              ))}
              <span className="ch-inbox-foot">
                <span>{inBranch(b.count)}</span>
                {b.answered && (
                  <span className="ch-inbox-tag ch-inbox-answered" title={answeredLine(b.answered)}>
                    <TickIcon size={11} /> Answered
                  </span>
                )}
                {b.unread > 0 && <span className="ch-badge">{b.unread > 99 ? '99+' : b.unread}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
